import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuthModal } from "@/context/AuthModalContext";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate } from "@/lib/date";
import { toast } from "sonner";
import { Ticket, Heart, Eye, Bookmark, MapPin, CalendarClock, Clock, ArrowLeft, ChevronDown, Hourglass, Coffee, Users, DoorOpen, Camera, Armchair, ArrowRight } from "lucide-react";
import { ActorAvatar } from "@/components/ActorAvatar";
import { Seo, showDescription } from "@/components/Seo";
import ShowCommunity from "@/components/ShowCommunity";
import { affiliateUrl, isAffiliate } from "@/lib/affiliate";

const FALLBACK = "https://images.unsplash.com/photo-1562329265-95a6d7a83440?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function CastCard({ c, former = false }) {
  const inner = (
    <>
      <ActorAvatar name={c.actor_name} photoUrl={c.photo_url} size={44} />
      <div className="min-w-0">
        <p className="font-medium leading-tight">{c.actor_name}</p>
        <p className="mt-0.5 break-words text-xs leading-snug text-muted-foreground">
          {c.role}
          {former && c.ends ? ` · left ${fmtDate(c.ends)}` : ""}
        </p>
      </div>
    </>
  );
  const base = `flex items-center gap-3 rounded-xl border border-border bg-card p-3 transition-colors ${
    former ? "opacity-60" : ""
  }`;
  if (c.actor_id) {
    return (
      <Link to={`/actors/${c.actor_id}`} title={c.role} className={`${base} hover:border-primary`}>
        {inner}
      </Link>
    );
  }
  return <div title={c.role} className={base}>{inner}</div>;
}

function InfoItem({ icon: Icon, tone, label, value }) {
  return (
    <div className="flex gap-3">
      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <dt className="text-sm text-muted-foreground">{label}</dt>
        <dd className="mt-0.5 text-amber-50/90">{value}</dd>
      </div>
    </div>
  );
}

// "checked today" next to a seller's price, from the date the daily price check last confirmed it. Old or missing dates show nothing.
function checkedLabel(iso) {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (isNaN(days) || days < 0 || days > 14) return "";
  return days === 0 ? "checked today" : days === 1 ? "checked yesterday" : `checked ${days} days ago`;
}

function priceNumber(t) {
  const m = (t.starting_from || "").match(/[\d,]+\.?\d*/);
  if (!m) return null;
  const v = parseFloat(m[0].replace(/,/g, ""));
  return isNaN(v) ? null : v;
}

export default function ShowDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const { openAuthModal } = useAuthModal();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [me, setMe] = useState(user);
  const [ticketMenuOpen, setTicketMenuOpen] = useState(false);
  const [showCompany, setShowCompany] = useState(false);
  const [showFormer, setShowFormer] = useState(false);
  const ticketMenuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ticketMenuRef.current && !ticketMenuRef.current.contains(e.target)) setTicketMenuOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
    } catch {
      setMe({ followed_shows: [], seen_shows: [], want_shows: [] });
    }
  }, []);

  useEffect(() => {
    api.get(`/shows/${id}`).then((r) => setShow(r.data)).catch(() => setShow(false));
    loadMe();
  }, [id, loadMe]);

  useEffect(() => {
    if (show && show.slug && id !== show.slug) {
      navigate(`/shows/${show.slug}`, { replace: true });
    }
  }, [show, id, navigate]);

  if (show === false) return (
    <div className="mx-auto max-w-3xl px-6 py-20 text-center font-serif text-2xl">
      <Seo title="Show not found" noindex />
      Show not found.
    </div>
  );
  if (!show || !me) return <div className="mx-auto max-w-7xl px-6 py-20 text-muted-foreground">Loading…</div>;


  const following = me.followed_shows?.includes(show.id);
  const seen = me.seen_shows?.includes(show.id);
  const want = me.want_shows?.includes(show.id);
  const castList = show.cast || [];
  const todayIso = new Date().toISOString().slice(0, 10);
  const isFormer = (c) => c.ends && c.ends.slice(0, 10) < todayIso;
  const currentCast = castList.filter((c) => !isFormer(c));
  const formerCast = castList.filter(isFormer);
  const principals = currentCast.filter((c) => (c.group || "Principal") === "Principal");
  const covers = currentCast.filter((c) => c.group === "Understudy" || c.group === "Swing");
  const ensemble = currentCast.filter(
    (c) => (c.group || "Principal") !== "Principal" && c.group !== "Understudy" && c.group !== "Swing"
  );
  const companyCount = covers.length + ensemble.length;
  const sellerPrices = (show.ticket_links || []).map(priceNumber).filter((p) => p !== null);
  const cheapest = sellerPrices.length > 1 ? Math.min(...sellerPrices) : null;
  const hasSeatCard = Boolean(show.seat_view_url || show.seatplan_url);
  const isClosed = show.status === "Closed";

  const followNow = async () => {
    const { data } = await api.post(`/watchlist/follow-show/${show.id}`);
    toast.success(data.following ? `Following ${show.name}` : `Unfollowed ${show.name}`);
    await loadMe(); refresh();
  };
  const seenNow = async () => {
    const { data } = await api.post(`/watchlist/seen/${show.id}`);
    toast.success(data.seen ? "Marked as seen" : "Removed from seen");
    await loadMe();
  };
  const wantNow = async () => {
    const { data } = await api.post(`/watchlist/want/${show.id}`);
    toast.success(data.want ? "Added to want to see" : "Removed from want to see");
    await loadMe();
  };
  const doFollow = () => (user ? followNow() : openAuthModal(followNow, `Sign in to follow ${show.name} and get alerts when its cast changes.`));
  const doSeen = () => (user ? seenNow() : openAuthModal(seenNow));
  const doWant = () => (user ? wantNow() : openAuthModal(wantNow));

  return (
    <div>
      <Seo
        title={`${show.name} tickets, cast & booking`}
        description={showDescription(show)}
        image={show.image_url}
        path={show.slug ? `/shows/${show.slug}` : undefined}
      />
      <section className="relative"></section>
      <section className="relative">
  {/* Blurred, colorful background fill */}
  <div className="absolute inset-0 overflow-hidden">
    <img
      src={show.image_url || FALLBACK}
      alt=""
      className="h-full w-full scale-125 object-cover opacity-80 blur-2xl"
    />
  </div>
  {/* Dark gradient overlay for text readability */}
  <div className="absolute inset-0 bg-gradient-to-b from-[#0D0A0E]/40 via-[#0D0A0E]/60 to-[#0D0A0E]/95" />
  {/* Sharp, unstretched poster, centered */}
  <div className="absolute inset-0 flex items-center justify-center">
    <img
      src={show.image_url || FALLBACK}
      alt={show.name}
      className="max-h-full max-w-full object-contain opacity-30"
    />
  </div>
  <div className="grain absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
          <Link to="/shows" className="inline-flex items-center gap-1.5 text-sm text-amber-100/70 transition-colors hover:text-amber-50">
            <ArrowLeft className="h-4 w-4" /> Browse shows
          </Link>
          <div className="mt-4"><StatusBadge status={show.status} testId="detail-status" /></div>

          <div className="mt-3 flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="font-serif text-4xl font-semibold text-amber-50 sm:text-5xl lg:text-6xl">{show.name}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-amber-100/80">
                <span className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {show.venue}</span>
                {show.theatre_slug && (
                  <Link
                    to={`/theatres/${show.theatre_slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#C89B3C] bg-[#C89B3C]/15 px-3.5 py-1.5 text-sm font-semibold text-[#C89B3C] transition-colors hover:bg-[#C89B3C]/30"
                  >
                    <Eye className="h-4 w-4" /> Where to sit
                  </Link>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3 sm:flex-shrink-0">
              <div className="relative" ref={ticketMenuRef}>
              {isClosed ? (
                <span className="inline-flex items-center rounded-full border border-white/20 bg-white/5 px-6 py-3 font-semibold text-amber-100/70">
                  Closed
                </span>
              ) : show.ticket_links && show.ticket_links.length > 0 ? (
                  <>
                    <button
                      onClick={() => setTicketMenuOpen((v) => !v)}
                      data-testid="detail-get-tickets"
                      className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground transition-transform hover:scale-105"
                    >
                      <Ticket className="h-4 w-4" /> Get Tickets
                      <ChevronDown className={`h-4 w-4 transition-transform ${ticketMenuOpen ? "rotate-180" : ""}`} />
                    </button>
                    {ticketMenuOpen && (
                                            <div className="absolute left-0 top-full z-[9999] mt-2 w-[calc(100vw-2rem)] max-w-xs overflow-hidden rounded-2xl border border-amber-900/20 bg-[#161118] p-2 shadow-xl sm:left-auto sm:right-0 sm:w-80 sm:max-w-none">
                        {show.ticket_links.map((t, i) => (
                          <a
                            key={i}
                            href={affiliateUrl(t.url)}
                            target="_blank"
                            rel={isAffiliate(t.url) ? "sponsored noopener noreferrer" : "noopener noreferrer"}
                            className={`flex items-center justify-between gap-4 rounded-xl px-4 py-2.5 text-sm font-medium text-amber-50 transition-colors hover:bg-white/10 ${
                              cheapest !== null && priceNumber(t) === cheapest ? "bg-[#C89B3C]/10 ring-1 ring-[#C89B3C]/50" : ""
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              {t.name}
                              {cheapest !== null && priceNumber(t) === cheapest && (
                                <span className="rounded-full bg-[#C89B3C] px-2 py-0.5 text-[11px] font-bold text-black">cheapest</span>
                              )}
                            </span>
                            {t.starting_from && (
                              <span className="text-right text-[#C89B3C]">
                                from {t.starting_from}
                                {checkedLabel(t.price_checked_at) && <span className="block text-[11px] font-normal text-amber-100/50" data-testid="price-checked">{checkedLabel(t.price_checked_at)}</span>}
                              </span>
                            )}
                          </a>
                        ))}
                          {(show.price_guide || show.ticket_links.length > 1) && (
                          <Link
                            to={`/shows/${show.slug || show.id}/prices`}
                            className="mt-1 flex items-center justify-between rounded-xl px-4 py-2.5 text-sm font-semibold text-[#C89B3C] transition-colors hover:bg-white/10"
                          >
                            Compare prices by seat area <ArrowRight className="h-4 w-4" />
                          </Link>
                        )}
                        <p className="px-4 pt-2 text-xs text-amber-100/50">Prices are indicative and may vary — check each site for current availability.
                          {show.ticket_links.some((t) => isAffiliate(t.url)) && " Some links are affiliate links: we may earn a commission if you book, at no extra cost to you."}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  show.ticket_link && (
                    <a
                      href={affiliateUrl(show.ticket_link)}
                      target="_blank"
                      rel={isAffiliate(show.ticket_link) ? "sponsored noopener noreferrer" : "noopener noreferrer"}
                      data-testid="detail-get-tickets"
                      className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground transition-transform hover:scale-105"
                    >
                      <Ticket className="h-4 w-4" /> Get Tickets
                    </a>
                  )
                )}
              </div>
              <button
                onClick={doFollow}
                data-testid="detail-follow-button"
                className={`inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition-colors ${
                  following ? "bg-amber-50 text-black" : "border border-amber-100/40 text-amber-50 hover:bg-white/10"
                }`}
              >
                <Heart className={`h-4 w-4 ${following ? "fill-current" : ""}`} /> {following ? "Following" : "Follow"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[700px]" aria-hidden="true">
          <img
            src={show.image_url || FALLBACK}
            alt=""
            className="h-full w-full scale-150 object-cover opacity-30 blur-3xl"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0A0E]/0 via-[#0D0A0E]/70 to-[#0D0A0E]" />
        </div>
      <div className="relative mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className={`grid gap-8 ${hasSeatCard ? "lg:grid-cols-3" : ""}`}>
          <div className={hasSeatCard ? "lg:col-span-2" : ""}>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-[#C89B3C]/30 bg-gradient-to-br from-[#C89B3C]/15 to-[#161118]/70 p-5 backdrop-blur">
                <div className="flex items-center gap-2 text-sm font-medium text-[#C89B3C]">
                  <CalendarClock className="h-4 w-4" /> Booking until
                </div>
                <p className="mt-2 font-serif text-xl font-semibold text-amber-50" data-testid="detail-booking-until">
                  {show.booking_until ? fmtDate(show.booking_until) : "—"}
                </p>
              </div>
              <div className="rounded-2xl border border-[#FF758F]/30 bg-gradient-to-br from-[#FF758F]/15 to-[#161118]/70 p-5 backdrop-blur">
                <div className="flex items-center gap-2 text-sm font-medium text-[#FF758F]">
                  <Clock className="h-4 w-4" /> Opened
                </div>
                <p className="mt-2 font-serif text-xl font-semibold text-amber-50">
                  {show.opening_date ? fmtDate(show.opening_date) : "—"}
                </p>
              </div>
              {show.next_performance && (
                <div className="rounded-2xl border border-[#B82332]/40 bg-gradient-to-br from-[#B82332]/25 to-[#161118]/70 p-5 backdrop-blur">
                  <div className="flex items-center gap-2 text-sm font-medium text-[#FF758F]">
                    <Clock className="h-4 w-4" /> Next performance
                  </div>
                  <p className="mt-2 font-serif text-xl font-semibold text-amber-50">{fmtDate(show.next_performance)}</p>
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                onClick={doSeen}
                data-testid="detail-seen-button"
                className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                  seen ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"
                }`}
              >
                <Eye className="h-4 w-4" /> {seen ? "Seen" : "Mark as seen"}
              </button>
              <button
                onClick={doWant}
                data-testid="detail-want-button"
                className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                  want ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/10 text-[hsl(var(--gold))]" : "border-border hover:bg-secondary"
                }`}
              >
                <Bookmark className="h-4 w-4" /> {want ? "Want to see" : "Add to want to see"}
              </button>
            </div>

            {show.synopsis && (
              <div className="mt-8 max-w-3xl border-l-4 border-[#B82332] pl-6">
                <h2 className="font-serif text-2xl font-semibold text-amber-50">About the show</h2>
                <p className="mt-3 font-serif text-lg leading-8 text-amber-50/85">{show.synopsis}</p>
              </div>
            )}
          </div>

          {hasSeatCard && (
                        <aside className="rounded-2xl border border-[#C89B3C]/40 bg-gradient-to-br from-[#C89B3C]/20 to-[#161118]/70 p-5 backdrop-blur lg:col-span-1 lg:self-start">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#C89B3C]/20 text-[#C89B3C]">
                            <Camera className="h-5 w-5" />
                          </div>
                          <h2 className="font-serif text-xl font-semibold leading-tight text-amber-50">
                            See the view from your seat
                          </h2>
                        </div>
                        <p className="mt-3 text-sm leading-6 text-amber-50/80">
                          Real photos from real seats at {show.venue}, so you know what you will see before you book.
                        </p>
                        <a
                          href={show.seat_view_url || show.seatplan_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group mt-4 inline-flex w-full items-center justify-between gap-3 rounded-full bg-[#C89B3C] px-5 py-3 font-semibold text-black transition-transform hover:scale-[1.02]"
                        >
                          Browse seat photos
                          <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                        </a>
                        {show.seat_view_url && show.seatplan_url && (
                          <a
                            href={show.seatplan_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#C89B3C] hover:underline"
                          >
                            <Armchair className="h-4 w-4" /> Seating plan and reviews on SeatPlan
                          </a>
                        )}
                        <p className="mt-3 text-xs text-amber-100/50">
                          Photos from {show.seat_view_url ? "A View From My Seat" : "SeatPlan"}. Opens in a new tab.
                        </p>
                      </aside>
          )}
        </div>

{(show.running_time || show.interval_info || show.age_guidance || show.arrival_info || show.schedule) && (
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-[#161118]/70 p-6 backdrop-blur lg:col-span-2">
              <h2 className="font-serif text-2xl font-semibold text-amber-50">More Information</h2>
              <dl className="mt-5 grid gap-5 sm:grid-cols-2">
                {show.running_time && (
                  <InfoItem icon={Hourglass} tone="bg-[#C89B3C]/15 text-[#C89B3C]" label="Running time" value={show.running_time} />
                )}
                {show.interval_info && (
                  <InfoItem icon={Coffee} tone="bg-[#FF758F]/15 text-[#FF758F]" label="Interval" value={show.interval_info} />
                )}
                {show.age_guidance && (
                  <InfoItem icon={Users} tone="bg-[#B82332]/25 text-[#FF758F]" label="Age guidance" value={show.age_guidance} />
                )}
                {show.arrival_info && (
                  <InfoItem icon={DoorOpen} tone="bg-[#C89B3C]/15 text-[#C89B3C]" label="Arrival" value={show.arrival_info} />
                )}
              </dl>
            </div>

            {show.schedule && (
              <div className="rounded-2xl border border-[#C89B3C]/25 bg-gradient-to-b from-[#C89B3C]/10 to-[#161118]/70 p-6 backdrop-blur lg:col-span-1">
                <h2 className="font-serif text-xl font-semibold text-amber-50">Weekly Schedule</h2>
                <ul className="mt-3 divide-y divide-white/10">
                  {show.schedule
                    .split(/\.\s*|,\s*(?=[A-Z][a-z]+:)/)
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((line, i) => {
                      const m = line.match(/^([A-Za-z][A-Za-z &/-]{1,40}):\s*(.+)$/);
                      return (
                        <li key={i} className="flex items-baseline justify-between gap-4 py-2.5 text-sm">
                          {m ? (
                            <>
                              <span className="font-medium text-[#C89B3C]">{m[1]}</span>
                              <span className="text-right text-amber-50/85">{m[2]}</span>
                            </>
                          ) : (
                            <span className="text-amber-50/85">{line}</span>
                          )}
                        </li>
                      );
                    })}
                </ul>
              </div>
            )}
          </div>
        )}

<div className="mt-10 space-y-10">
        <div>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4">
            <h2 className="flex items-center gap-3 font-serif text-2xl font-semibold text-amber-50">
                <span className="h-6 w-1 rounded-full bg-[#C89B3C]" /> Current Cast
              </h2>
              {castList.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {principals.length} principal{principals.length === 1 ? "" : "s"}
                  {companyCount > 0 ? ` · ${companyCount} in the wider company` : ""}
                </span>
              )}
            </div>

            {castList.length === 0 && <p className="mt-4 text-muted-foreground">No cast recorded yet.</p>}

            {principals.length > 0 && (
              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {principals.map((c, i) => <CastCard key={`p-${i}`} c={c} />)}
              </div>
            )}

            {companyCount > 0 && (
              <div className="mt-5">
                <button
                  onClick={() => setShowCompany((v) => !v)}
                  className="rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary"
                >
                  {showCompany ? "Hide understudies and ensemble" : `Show understudies, swings and ensemble (${companyCount})`}
                </button>
                {showCompany && (
                  <div className="mt-4 space-y-5">
                    {covers.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Understudies, standbys and swings
                        </h3>
                        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                          {covers.map((c, i) => <CastCard key={`u-${i}`} c={c} />)}
                        </div>
                      </div>
                    )}
                    {ensemble.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Ensemble</h3>
                        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                          {ensemble.map((c, i) => <CastCard key={`e-${i}`} c={c} />)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {formerCast.length > 0 && (
              <div className="mt-5">
                <button
                  onClick={() => setShowFormer((v) => !v)}
                  className="text-sm text-muted-foreground underline-offset-4 hover:underline"
                >
                  {showFormer ? "Hide former cast" : `Former cast (${formerCast.length})`}
                </button>
                {showFormer && (
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    {formerCast.map((c, i) => <CastCard key={`f-${i}`} c={c} former />)}
                  </div>
                )}
              </div>
            )}
          </div>

          <ShowCommunity show={show} user={user} onSignIn={() => openAuthModal(() => {}, `Sign in to see what people are saying about ${show.name} and to share your own thoughts.`)} />

          <div className="max-w-2xl">
          <h2 className="flex items-center gap-3 font-serif text-2xl font-semibold text-amber-50">
            <span className="h-6 w-1 rounded-full bg-[#FF758F]" /> Cast Change History
          </h2>
            <div className="mt-4 space-y-3">
              {(!show.cast_events || show.cast_events.length === 0) && <p className="text-muted-foreground">No changes logged yet.</p>}
              {show.cast_events?.map((e) => (
                <div key={e.id} data-testid={`cast-event-${e.id}`} className="relative rounded-xl border border-border bg-card p-4 pl-5">
                  <span className="absolute left-0 top-4 h-8 w-1 rounded-full bg-[#FF758F]" />
                  <p className="font-medium">{e.description}</p>
                  {e.note && <p className="mt-1 text-sm text-muted-foreground">{e.note}</p>}
                  <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{fmtDate(e.change_date)}</span>
                    {e.source_link && (
                      <a href={e.source_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Source
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}